import get from 'lodash/get'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy'
import NavigationService from '@disco/state-management/NavigationService'
import type { ActionStatus } from 'types/explorer'
import type { Playlist, PlaylistId, PlaylistTheme } from 'types/playlist'
import type { GetState, Thunk } from 'types/redux'
import type { NativeTag, SelectableTrack, Track, TrackId } from 'types/track'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import api from '@disco/api/server'
import {
  DATA_LOAD,
  NESTING_LOAD,
  NESTING_LOAD_ERROR,
  NESTING_TRACKS_LOAD_SUCCESS,
  PLAYLIST_ACTION_STATUS_SET,
  PLAYLIST_LOAD_BATCH,
  TRACK_ALBUM_ARTWORK_UPDATE,
  TRACK_LOAD_BATCH,
  TRACK_MERGE,
  TRACK_MERGE_ERROR,
  TRACK_MERGE_SUCCESS,
  TRACK_UPDATE,
  TRACK_UPDATE_ERROR,
  TRACK_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import { ARTIST, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getMainTracksIdsForFetch,
  getNestedTracksIdsForFetch,
} from '@disco/state-management/selectors/nesting'
import { getTrack } from '@disco/state-management/selectors/tracks'
import { parseServerError } from '@disco/utils/captureError'
import { convertMixedMetadataToBasicMetadata } from '@disco/utils/commonMetadata'
import { getNounForExt } from '@disco/utils/file-utils'
import { pushUINotification } from '@disco/utils/notifications'
import { mediaItemsToString } from '@disco/utils/string-utils'
/**
 * Loads a batch of mixed items (tracks, playlists, artists) into the Redux store.
 * Separates items by type and dispatches appropriate actions for each type.
 * @param batch - Array of mixed items to load into the store
 * @param resetPlaylistInfo - Whether to reset playlist info when loading tracks
 * @returns Dispatch function to load the batch
 */
export function loadBatch(
  batch: Array<any>,
  resetPlaylistInfo = true,
): Record<string, any> {
  return (dispatch: (...args: Array<any>) => any) => {
    if (!batch || batch.length === 0) return
    const artists = batch.filter(item => item && item.type === ARTIST)

    if (artists.length > 0) {
      dispatch({
        type: DATA_LOAD,
        data: {
          artists: artists.map(item => omit(item, ['type'])),
        },
      })
    }

    const playlists = batch.filter(item => item && item.type === PLAYLIST)

    if (playlists.length > 0) {
      dispatch({
        type: PLAYLIST_LOAD_BATCH,
        list: playlists,
      })
    }

    const tracks = batch.filter(item => item && item.type === TRACK)

    if (tracks.length > 0) {
      const nestedTracks = tracks.flatMap(track => track.nested_tracks ?? [])
      dispatch({
        type: TRACK_LOAD_BATCH,
        list: [...tracks, ...nestedTracks],
        resetPlaylistInfo,
      })
    }
  }
}
/**
 * Merges multiple tracks into a single master track.
 * @param masterTrack - The primary track to merge others into
 * @param tracksToMerge - Array of tracks to merge into the master
 * @param losslessFormatTrack - Optional lossless format track to include
 * @returns Thunk that performs the merge operation and handles success/error states
 */
export function mergeTracks(
  masterTrack?: SelectableTrack,
  tracksToMerge: SelectableTrack[] = [],
  losslessFormatTrack?: SelectableTrack,
  redirectTo = '/',
): (dispatch: (...args: Array<any>) => any) => Promise<void> {
  return (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: TRACK_MERGE,
      masterTrack,
      tracksToMerge,
    })
    return api
      .mergeTracks(masterTrack, tracksToMerge, losslessFormatTrack)
      .then(() => {
        dispatch(
          saveTrackMetadata({
            showNotifications: false,
          }),
        )
        dispatch({
          type: TRACK_MERGE_SUCCESS,
          masterTrack,
          tracksToMerge,
        })
        NavigationService.push(redirectTo)
        pushUINotification({
          message: 'Tracks merged! Great job.',
          level: 'success',
        })
        dispatch(sendMixpanelEvent('Merge tracks'))
      })
      .catch(error => {
        if (FRONTEND_VERSION === 'v2') {
          const items = [masterTrack, ...tracksToMerge]
          const intercomMsg = `I'm having trouble merging ${mediaItemsToString(
            items,
            'id',
          )}.`
          pushUINotification({
            message: `There was an error merging the ${mediaItemsToString(
              items,
            )}. Click |${JSON.stringify({
              retryMessage: 'here',
            })}| to try again. If you’re still having trouble, |${JSON.stringify(
              {
                contactUsLink: 'let us know.',
                intercomMsg: intercomMsg,
              },
            )}|`,
            retry: () =>
              dispatch(
                mergeTracks(masterTrack, tracksToMerge, losslessFormatTrack),
              ),
            level: 'error',
            dismissible: 'button',
          })
        }

        dispatch({
          type: TRACK_MERGE_ERROR,
          masterTrack,
          tracksToMerge,
          error,
        })
      })
  }
}
/**
 * Sorts tracks within a playlist by the specified criteria.
 * @param playlist - The playlist containing tracks to sort
 * @param sortKey - The field to sort by (e.g., name, artist, date)
 * @param sortDirection - The direction to sort (ascending or descending)
 * @returns Thunk that sorts playlist tracks and tracks analytics
 */
export function sortPlaylistTracks(
  playlist: Playlist,
  sortKey: SORT,
  sortDirection: SORT_DIRECTION,
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch(
      setPlaylistActionStatus(playlist, {
        sorting: true,
      }),
    )
    dispatch(resetSelection())
    api
      .sortPlaylistTracks(playlist, sortKey, sortDirection)
      .then(response => {
        // it will set action status on its own
        mapResponseToReducerObjects(dispatch, getState, response)
        dispatch(sendMixpanelEvent('Change track order explorer'))
      })
      .catch(error => {
        dispatch(sendGenericError(error))
        dispatch(
          setPlaylistActionStatus(playlist, {
            sorting: false,
          }),
        )
      })
  }
}
/**
 * Sets the action status for a playlist (e.g., loading, sorting states).
 * @param playlist - The playlist to update status for
 * @param status - Partial status object to merge with current status
 * @returns Action object to update playlist action status
 */
export function setPlaylistActionStatus(
  playlist: Playlist,
  status: Partial<ActionStatus>,
): {
  type: string
  id: PlaylistId
  status: Partial<ActionStatus>
} {
  return {
    type: PLAYLIST_ACTION_STATUS_SET,
    id: playlist.playlist_id,
    status,
  }
}
/**
 * Changes the visual theme of a playlist.
 * @param playlist - The playlist to update the theme for
 * @param theme - The new theme to apply to the playlist
 * @returns Thunk that updates the playlist theme and tracks analytics
 */
export function changePlaylistTheme(
  playlist: Playlist,
  theme: PlaylistTheme,
): (dispatch: any, getState: GetState) => Promise<unknown> {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    return api
      .changePlaylistTheme(playlist, theme)
      .then(result => {
        dispatch(sendMixpanelEvent('Attach theme from playlist menu'))
        mapResponseToReducerObjects(dispatch, getState, result)
      })
      .catch(error => dispatch(sendGenericError(error)))
  }
}
/**
 * Applies and removes native tags from multiple tracks.
 * @param trackIds - Array of track IDs to update
 * @param addIds - Array of tag IDs to add to the tracks
 * @param removeIds - Array of tag IDs to remove from the tracks
 * @returns Thunk that updates track tags and shows success/error notifications
 */
export function applyNativeTagsToTracks(
  trackIds: TrackId[],
  addIds: NativeTag['id'][],
  removeIds: NativeTag['id'][],
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: TRACK_UPDATE,
    })

    try {
      await api.applyNativeTagsToTracks(trackIds, addIds, removeIds)

      for (const trackId of trackIds) {
        // TODO: handle failure
        const originalTrack = getState().tracks[trackId]
        const nativeTags = addIds.map(
          tagId => getState().entities.nativeTags[tagId],
        )
        const updatedTrack = mapResponseToReducerObjects(dispatch, getState, {
          ...originalTrack,
          native_tags: uniqBy(
            [
              ...(originalTrack.native_tags ?? []).filter(
                tag => !removeIds.includes(tag.id),
              ),
              ...nativeTags,
            ],
            'id',
          ),
        })
        dispatch({
          type: TRACK_UPDATE_SUCCESS,
          updatedTrack,
        })
      }

      pushUINotification({
        message: 'New tags added!',
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: TRACK_UPDATE_ERROR,
        error,
      })
      throw error
    }
  }
}
/**
 * Adds a single native tag to multiple tracks.
 * @param trackIds - Array of track IDs to add the tag to
 * @param tag - The native tag to add to all specified tracks
 * @returns Thunk that adds the tag and shows success/error notifications
 */
export function addNativeTagToTracks(
  trackIds: TrackId[],
  tag: NativeTag,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: TRACK_UPDATE,
    })

    try {
      await api.applyNativeTagsToTracks(trackIds, [tag.id], [])

      for (const trackId of trackIds) {
        // TODO: handle failure
        const originalTrack = getState().tracks[trackId]
        const updatedTrack = mapResponseToReducerObjects(dispatch, getState, {
          ...originalTrack,
          native_tags: uniqBy(
            (originalTrack.native_tags ?? []).concat(tag),
            'id',
          ),
        })
        dispatch({
          type: TRACK_UPDATE_SUCCESS,
          updatedTrack,
        })
      }

      pushUINotification({
        title: 'New tag added!',
        message: `“${tag.name}” (${
          getState().entities.nativeCategories[tag.category_id].name
        })`,
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: TRACK_UPDATE_ERROR,
        error,
      })
    }
  }
}
/**
 * Removes a single native tag from multiple tracks.
 * @param trackIds - Array of track IDs to remove the tag from
 * @param tag - The native tag to remove from all specified tracks
 * @returns Thunk that removes the tag and shows success/error notifications
 */
export function removeNativeTagFromTracks(
  trackIds: TrackId[],
  tag: NativeTag,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    dispatch({
      type: TRACK_UPDATE,
    })

    try {
      await api.applyNativeTagsToTracks(trackIds, [], [tag.id])

      for (const trackId of trackIds) {
        // TODO: handle failure
        const originalTrack = getState().tracks[trackId]
        const updatedTrack = mapResponseToReducerObjects(dispatch, getState, {
          ...originalTrack,
          native_tags: (originalTrack.native_tags ?? []).filter(
            t => t.id !== tag.id,
          ),
        })
        dispatch({
          type: TRACK_UPDATE_SUCCESS,
          updatedTrack,
        })
      }

      pushUINotification({
        title: 'Tag removed!',
        message: `“${tag.name}” (${
          getState().entities.nativeCategories[tag.category_id].name
        })`,
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: TRACK_UPDATE_ERROR,
        error,
      })
    }
  }
}
/**
 * Saves track metadata including artwork and basic metadata fields.
 * @param options - Configuration options for saving
 * @param options.imageFile - Optional image file to use as artwork
 * @param options.overrideAlbumArtwork - Whether to override album artwork
 * @param options.showNotifications - Whether to show success/error notifications
 * @returns Thunk that saves track metadata and handles success/error states
 */
export function saveTrackMetadata(
  options = {} as {
    imageFile?: File | null | undefined
    overrideAlbumArtwork?: boolean
    showNotifications?: boolean
    successMessage?: string
  },
): (dispatch: any, getState: GetState) => Promise<void> {
  const {
    overrideAlbumArtwork = true,
    showNotifications = true,
    successMessage = 'Saved!',
  } = options
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { id, metadata } = getState().server.editedTrack
    dispatch({
      type: TRACK_UPDATE,
    })
    const fullMetadata = convertMixedMetadataToBasicMetadata(metadata)
    const basicMetadata = omit(fullMetadata, ['Artwork', 'Lyrics_source'])

    if (basicMetadata['External Id']) {
      basicMetadata['External Id'] = basicMetadata['External Id'].trim()
    }

    let artworkMetadata: (File | string) | null | undefined =
      fullMetadata.Artwork
    if (!id) return
    const track = getState().tracks[id]
    const artwork = get(track, 'artwork_sizes.full', track.artwork)

    try {
      if (artworkMetadata === 'IMAGE_FILE' && options.imageFile) {
        artworkMetadata = options.imageFile
      }

      if (artworkMetadata === null) {
        await api.updateTrackArtwork(id, null, overrideAlbumArtwork)
      } else if (
        typeof artworkMetadata !== 'string' &&
        artworkMetadata &&
        artworkMetadata.type &&
        artworkMetadata !== artwork
      ) {
        await api.updateTrackArtwork(id, artworkMetadata, overrideAlbumArtwork)
      }

      const resp = await api.updateTrackMetadata(id, basicMetadata)
      const updatedTrack = mapResponseToReducerObjects(dispatch, getState, resp)
      dispatch({
        type: TRACK_UPDATE_SUCCESS,
        updatedTrack,
      })

      if (overrideAlbumArtwork && updatedTrack.type === TRACK) {
        dispatch({
          type: TRACK_ALBUM_ARTWORK_UPDATE,
          album: updatedTrack.album,
          artist: updatedTrack.artist,
          artwork: updatedTrack.artwork,
          artwork_sizes: updatedTrack.artwork_sizes,
        })
      }

      if (showNotifications) {
        pushUINotification({
          message: successMessage,
          level: 'success',
        })
      }
    } catch (error) {
      if (showNotifications) {
        if (error.body && Array.isArray(error.body)) {
          pushUINotification({
            message: error.body[0],
            level: 'error',
          })
        } else {
          const file = getNounForExt(track.ext)
          const intercomMsg = `I’m having trouble editing ${file} "${track.name}" (${track.id}).`
          const message = `There was an error saving the ${file} "${
            track.name
          }". Click |${JSON.stringify({
            retryMessage: 'here',
          })}| to try again. If you’re still having trouble, |${JSON.stringify({
            contactUsLink: 'let us know.',
            intercomMsg,
          })}|`
          pushUINotification({
            // grep-friendly example:
            // 'Sorry! A server error prevented this track from saving. Please try again, and if it fails, let us know.'
            message,
            retry: () => dispatch(saveTrackMetadata(options)),
            dismissible: 'button',
            level: 'error',
          })
        }
      }

      dispatch({
        type: TRACK_UPDATE_ERROR,
        error,
      })
    }
  }
}

function mapResponseObjectToReducerObject(
  getState: GetState,
  entity,
): Record<string, any> {
  switch (entity.type) {
    case ARTIST:
      return { ...getState().artists.items[entity.id], type: ARTIST }

    case PLAYLIST:
      return getState().playlists[entity.playlist_id]

    case TRACK:
      return getState().tracks[entity.id]

    default:
      return entity
  }
}

/**
 * Maps API response objects to Redux store objects and loads them into the store.
 * Handles single objects, arrays, and paginated results with proper type mapping.
 * @param dispatch - Redux dispatch function
 * @param getState - Redux getState function for accessing current state
 * @param response - API response containing tracks, playlists, or artists
 * @returns Mapped response objects that reference store entities
 */
export function mapResponseToReducerObjects(
  dispatch: (...args: Array<any>) => any,
  getState: GetState, // TODO: add exact type
  response: Record<string, any>, // | { results: (Track | Playlist | Artist)[] }
): // | (Track | Playlist | Artist)[]
// | (Track | Playlist | Artist),
Record<string, any> {
  if (
    typeof response.results !== 'undefined' &&
    Array.isArray(response.results)
  ) {
    dispatch(loadBatch(response.results))
    return {
      ...response,
      results: response.results.map(item =>
        mapResponseObjectToReducerObject(getState, item),
      ),
    }
  } else if (Array.isArray(response)) {
    dispatch(loadBatch(response))
    return response.map(item =>
      mapResponseObjectToReducerObject(getState, item),
    )
  }

  dispatch(loadBatch([response]))
  return mapResponseObjectToReducerObject(getState, response)
}
/**
 * Gets a track from the store or fetches it from the server if not present.
 * @param id - The ID of the track to get or fetch
 * @param sourceLibraryId - Optional source library ID for aggregated search tracks
 * @returns Thunk that returns the track from store or fetches from server
 */
export const getOrFetchTrack =
  (
    id: TrackId,
    sourceLibraryId?: number,
  ): ((
    dispatch: (...args: Array<any>) => any,
    getState: GetState,
  ) => Promise<Track>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const existingTrack = getTrack(getState(), {
      id,
    })
    if (existingTrack) return existingTrack
    let track: Track | null = null

    if (sourceLibraryId) {
      track = await api.getAggregatedSearchTrack(id, sourceLibraryId)
    } else {
      track = await api.getTrack(id)
    }

    dispatch(loadBatch([track]))
    return track
  }
/**
 * Loads nested tracks (versions/stems) for a specific parent track.
 * @param id - The ID of the parent track to load nested tracks for
 * @returns Thunk that fetches and loads nested tracks
 */
export function loadNestedTracks(id: TrackId): Thunk {
  return async (dispatch, getState) => {
    try {
      dispatch({
        type: NESTING_LOAD,
        id,
      })
      const state = getState()
      const track = getTrack(state, {
        id,
      })

      if (isAggregatedSearchTrack(track)) {
        // TODO: When nesting becomes available - enable fetching
        dispatch({
          type: NESTING_TRACKS_LOAD_SUCCESS,
          id,
          nested_ids: [],
        })
        return
      }

      const parentTrack = await api.getTrack(id)
      mapResponseToReducerObjects(dispatch, getState, parentTrack)
      const nestedTracks = parentTrack.nested_tracks ?? []
      dispatch({
        type: NESTING_TRACKS_LOAD_SUCCESS,
        id,
        nested_ids: nestedTracks.map(t => t.id),
      })
    } catch (error) {
      dispatch(sendGenericError(error))
      dispatch({
        type: NESTING_LOAD_ERROR,
        id,
        error,
      })
    }
  }
}
/**
 * Fetches nested tracks for multiple master tracks from the server.
 * Only fetches if track nesting feature is enabled and tracks are not already loaded.
 * @param masterTracks - Array of master track IDs to fetch nested tracks for
 * @returns Thunk that fetches nested tracks and loads them into the store
 */
export const fetchNestedTracks =
  (
    masterTracks: TrackId[],
    { includePlaylistsCount = false }: { includePlaylistsCount?: boolean } = {},
  ): ((dispatch: any, getState: GetState) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const state = getState()
    // bailout if feature is not enabled as business may switch on/off feature
    // while keeping nesting relationships
    const nestingEnabled = isFeatureEnabled(state, {
      featureName: TRACK_NESTING,
    })
    if (!nestingEnabled) return
    // "main" tracks from search, browse or starred panels don't contain nested_tracks
    const mainTrackIds = getMainTracksIdsForFetch(state, masterTracks)
    const trackIds = [
      ...getNestedTracksIdsForFetch(state, masterTracks),
      ...mainTrackIds,
    ]
    if (trackIds.length === 0) return

    try {
      const response = await api.getExistingObjects(null, null, trackIds, {
        includePlaylistsCount,
      })
      const nestedTracks = Object.values(response.tracks)
      dispatch(loadBatch(nestedTracks))

      if (mainTrackIds.length !== 0) {
        dispatch(fetchNestedTracks(mainTrackIds, { includePlaylistsCount }))
      }
    } catch (error) {
      dispatch(sendGenericError(error))
    }
  }
